import type {
  ControlPlaneOperationalProjection,
  WorkspaceCleanupState,
} from '@internal/spassu-code-contract';
import { Box, Tooltip, Typography, makeStyles } from '@material-ui/core';

const useStyles = makeStyles(theme => ({
  cell: {
    display: 'grid',
    gap: theme.spacing(0.25),
  },
  detail: {
    color: theme.palette.text.secondary,
  },
}));

const CYCLE_LABELS: Record<string, string> = {
  preparing: 'Preparando',
  active: 'Ativa',
  completed: 'Concluída',
  cancelled: 'Cancelada',
  error: 'Erro',
};

const CONNECTION_LABELS: Record<string, string> = {
  not_started: 'Não iniciada',
  awaiting_pairing: 'Aguardando pareamento',
  connecting: 'Conectando',
  connected: 'Conectada',
  expired: 'Expirada',
  revoked: 'Revogada',
  failed: 'Falhou',
  unavailable: 'Indisponível',
};

const EXPIRY_LABELS: Record<string, string> = {
  none: '',
  two_hours: 'expira em < 2h',
  thirty_minutes: 'expira em < 30min',
  expired: 'validade expirada',
};

const WORKSPACE_LABELS: Partial<Record<WorkspaceCleanupState, string>> = {
  requested: 'Exclusão local pendente',
  claimed: 'Exclusão local em andamento',
  retryable_failed: 'Exclusão local pendente',
  overridden: 'Exclusão local ignorada',
};

function HandoffDetail({ children }: { children: string }) {
  const classes = useStyles();
  return (
    <Typography variant="caption" className={classes.detail}>
      {children}
    </Typography>
  );
}

function StalledConnectionAlert() {
  return (
    <Typography component="span" variant="caption" color="error" role="alert">
      Conexão atrasada — verifique o agente.
    </Typography>
  );
}

/**
 * The activity cycle, rendered as its OWN column. Independent from the connection
 * so a row can read "Ativa" and "Expirada" at the same time (ADR-001).
 */
export function ControlPlaneCycleCell({
  projection,
  fallbackStatus,
}: {
  projection?: ControlPlaneOperationalProjection;
  fallbackStatus: string;
}) {
  const label = projection
    ? CYCLE_LABELS[projection.activityCycle]
    : CYCLE_LABELS[fallbackStatus];
  return <Typography variant="body2">{label ?? fallbackStatus}</Typography>;
}

/**
 * Connection and workspace in one column, per row. No global modal and no
 * per-row request: the projection arrives with the page in a single batched read
 * (IT-013).
 */
interface ControlPlaneHandoffSummaryProps {
  projection?: ControlPlaneOperationalProjection;
  /**
   * The ingestion's stalled-connection signal. It stays a separate input because
   * "the agent is late" is an observation about ingestion, not a durable
   * connection state — but the portfolio must keep surfacing it as an actionable
   * alert (US-003), which is what this column replaced.
   */
  stalled?: boolean;
}

export function ControlPlaneHandoffSummary({
  projection,
  stalled = false,
}: ControlPlaneHandoffSummaryProps) {
  const classes = useStyles();
  if (!projection) {
    return (
      <Typography variant="body2" className={classes.detail}>
        —
      </Typography>
    );
  }
  const connection = CONNECTION_LABELS[projection.connection.state] ?? '—';
  const expiry = EXPIRY_LABELS[projection.connection.expiryBand] ?? '';
  const workspace = WORKSPACE_LABELS[projection.workspace.state];
  const staleNote = projection.stale
    ? 'Leitura desatualizada; mostrando o último estado conhecido.'
    : '';
  return (
    <Tooltip title={[expiry, workspace, staleNote].filter(Boolean).join(' · ')}>
      <Box className={classes.cell}>
        <Typography variant="body2">{connection}</Typography>
        {expiry ? <HandoffDetail>{expiry}</HandoffDetail> : null}
        {workspace ? <HandoffDetail>{workspace}</HandoffDetail> : null}
        {staleNote ? <HandoffDetail>{staleNote}</HandoffDetail> : null}
        {stalled ? <StalledConnectionAlert /> : null}
      </Box>
    </Tooltip>
  );
}

export const ControlPlaneConnectionCell = ControlPlaneHandoffSummary;
